import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, UsersThree, Trash, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export default function HouseholdPage() {
  const [hh, setHh] = useState(null);
  const [invites, setInvites] = useState([]);
  const [acceptCode, setAcceptCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [hhName, setHhName] = useState("");
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const saveName = async () => {
    if (!hhName.trim()) return;
    try {
      await api.patch("/households/me", { name: hhName.trim() });
      toast.success("Household renamed");
      setEditingName(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename");
    }
  };

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/households/me"), api.get("/households/invites")]);
    setHh(a.data);
    setInvites(b.data);
  };
  useEffect(() => { load(); }, []);

  const newInvite = async () => {
    try {
      setBusy(true);
      await api.post("/households/invites");
      await load();
    } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    await api.delete(`/households/invites/${id}`);
    await load();
  };
  const accept = async () => {
    if (!acceptCode.trim()) return;
    try {
      setBusy(true);
      await api.post("/households/invites/accept", { code: acceptCode.trim() });
      toast.success("Joined household");
      setAcceptCode("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };
  const inviteUrl = (code) => `${window.location.origin}/invite/${code}`;
  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };

  return (
    <div className="min-h-screen pb-32" data-testid="household-page">
      <header className="px-5 pt-6 pb-3 max-w-md mx-auto w-full">
        <Logo />
        <h1 className="font-heading text-3xl font-bold mt-3">Household</h1>
      </header>

      <main className="px-5 max-w-md mx-auto w-full space-y-5">
        <div className="tactile-card p-5">
          <div className="flex items-center gap-2">
            <UsersThree size={22} className="text-denim" />
            {editingName ? (
              <div className="flex items-center gap-1 flex-1" data-testid="hh-rename-editor">
                <Input
                  data-testid="hh-rename-input"
                  value={hhName}
                  onChange={(e) => setHhName(e.target.value)}
                  className="flex-1 min-h-[40px] bg-chalk border-2 border-stone_border rounded-lg"
                  autoFocus
                />
                <Button data-testid="hh-rename-save" onClick={saveName} className="bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[40px] font-bold">Save</Button>
                <Button data-testid="hh-rename-cancel" onClick={() => setEditingName(false)} variant="outline" className="border-2 border-stone_border min-h-[40px]">×</Button>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-xl font-bold flex-1" data-testid="hh-name">{hh?.name || "Your home"}</h2>
                {hh?.can_rename && (
                  <button
                    data-testid="hh-rename-btn"
                    onClick={() => { setHhName(hh?.name || ""); setEditingName(true); }}
                    className="text-xs text-denim font-bold underline"
                  >Rename</button>
                )}
              </>
            )}
          </div>
          <p className="label-cap mt-3">Members · {hh?.members?.length || 0}</p>
          <ul className="mt-2 space-y-2" data-testid="household-members">
            {hh?.members?.map((m) => {
              const displayName = m.name || m.email || "?";
              const initials = (m.name
                ? m.name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("")
                : (m.email || "?")[0]
              ).toUpperCase();
              return (
                <li key={m.id} data-testid={`hh-member-${m.user_id}`} className="flex items-center gap-3 border-b border-stone_border last:border-0 py-2">
                  <div className={`w-10 h-10 rounded-full font-heading font-bold flex items-center justify-center flex-shrink-0 ${
                    m.role === "owner" ? "bg-masking text-timber-900" : "bg-kraft text-timber-900 border-2 border-stone_border"
                  }`}>
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-semibold text-timber-900 truncate">{displayName}</p>
                      {m.is_you && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-denim bg-chalk border border-denim rounded-full px-1.5">You</span>
                      )}
                    </div>
                    {m.name && <p className="text-xs text-timber-500 truncate">{m.email}</p>}
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 ${
                        m.role === "owner"
                          ? "bg-masking text-timber-900"
                          : "bg-kraft text-timber-700 border border-stone_border"
                      }`}>
                        {m.role}
                      </span>
                      {m.is_admin && (
                        <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 bg-denim text-chalk">Admin</span>
                      )}
                      <span className="text-[10px] text-timber-500 font-mono">
                        joined {new Date(m.joined_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="tactile-card p-5">
          <p className="label-cap">Share invite link</p>
          <p className="text-sm text-timber-700 mt-1">
            Only approved users with this invite link can request to join this household.
          </p>
          <Button
            data-testid="create-invite-btn"
            onClick={newInvite}
            disabled={busy}
            className="mt-3 bg-masking text-timber-900 hover:bg-masking-600 hover:text-white font-bold min-h-[52px]"
          >
            Create invite link
          </Button>
          <ul className="mt-4 space-y-2">
            {invites.map((iv) => (
              <li key={iv.id} className="border-2 border-stone_border rounded-xl p-3 bg-chalk" data-testid={`invite-${iv.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-base font-bold text-timber-900">{iv.code}</code>
                  <div className="flex gap-1">
                    <button
                      data-testid={`invite-copy-code-${iv.id}`}
                      onClick={() => copy(iv.code)}
                      className="p-2 text-timber-700"
                      title="Copy code"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      data-testid={`invite-copy-link-${iv.id}`}
                      onClick={() => copy(inviteUrl(iv.code))}
                      className="p-2 text-timber-700 text-xs font-bold"
                      title="Copy link"
                    >
                      LINK
                    </button>
                    <button
                      data-testid={`invite-revoke-${iv.id}`}
                      onClick={() => revoke(iv.id)}
                      className="p-2 text-block"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-timber-500 mt-1 truncate">{inviteUrl(iv.code)}</p>
                <p className="text-[11px] text-timber-500 mt-0.5 font-mono">
                  Single-use · {iv.use_count}/{iv.max_uses} used
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="tactile-card p-5">
          <p className="label-cap">Have a code?</p>
          <p className="text-sm text-timber-700 mt-1">Paste it to join an existing household.</p>
          <div className="flex gap-2 mt-3">
            <Input
              value={acceptCode}
              onChange={(e) => setAcceptCode(e.target.value)}
              placeholder="invite code"
              data-testid="accept-code-input"
              className="min-h-[52px] bg-chalk border-2 border-stone_border rounded-xl font-mono"
            />
            <Button
              onClick={accept}
              disabled={busy}
              data-testid="accept-code-btn"
              className="bg-denim text-chalk hover:bg-denim-600 min-h-[52px] font-bold"
            >
              Join
            </Button>
          </div>
        </div>

        <div className="tactile-card p-5">
          <p className="label-cap">Signed in</p>
          <p className="font-semibold text-timber-900 mt-1" data-testid="signed-in-email">{user?.email}</p>
          {user?.is_admin && (
            <Button
              data-testid="open-admin-btn"
              onClick={() => navigate("/admin")}
              variant="outline"
              className="mt-3 w-full border-2 border-denim text-denim min-h-[52px] font-bold"
            >
              <ShieldCheck size={18} weight="duotone" className="mr-1" /> Admin · manage beta access
            </Button>
          )}
          <Button
            data-testid="open-feedback-btn"
            onClick={() => navigate("/feedback")}
            variant="outline"
            className="mt-3 w-full border-2 border-stone_border min-h-[52px] font-bold"
          >
            Send feedback or report a bug
          </Button>
          <Button
            data-testid="sign-out-btn"
            onClick={() => { signOut(); window.location.href = "/login"; }}
            variant="outline"
            className="mt-3 w-full border-2 border-stone_border min-h-[52px]"
          >
            Sign out
          </Button>
        </div>
      </main>
    </div>
  );
}
